import React, { useState, useEffect, useRef } from 'react';
import useSidebarResizing from '../hooks/useSidebarResizing';
import { useDispatch, useSelector } from 'react-redux';
import DjChangeBanner from './DjChangeBanner';
import { wrappablePath } from '../utils/formatPath';
import { formatDjImportSummary, formatDjImportStopped } from '../utils/djImportSummary';
import SendToAppModal from './SendToAppModal';
import SyncFromAppModal from './SyncFromAppModal';
import { getImportExclusions, localPlaylistSelectionKey } from '../utils/djImportSelection';
import AutoSyncToggle from './AutoSyncToggle';
import DjLibraryMeter from './DjLibraryMeter';
import DjLocationControls from './DjLocationControls';
import { persistDjSidebarWidth, DJ_SIDEBAR_MIN_WIDTH, DJ_SIDEBAR_MAX_WIDTH } from '../redux/utils/djSidebarWidth';
import useAutoSyncNudge from '../hooks/useAutoSyncNudge';
import virtualdjLogo from '../assets/virtual-dj-logo.png';
import { track } from '../utils/analytics';
import { clearSyncRequest } from '../redux/slices/djLibraryChangesSlice';
import { Button, Spinner, ListGroup, Badge } from 'react-bootstrap';
import ErrorBanner from './ErrorBanner';
import {
    loadPersistedVirtualDJState,
    toggleVirtualDJSidebar,
    setVirtualDJSidebarWidth,
    setVirtualDJSyncStatus,
    setVirtualDJPath,
    setVirtualDJExists,
    setVirtualDJPlaylists,
    setVirtualDJLastSyncTime,
    setVirtualDJImporting,
    setVirtualDJExporting,
    setVirtualDJError
} from '../redux/slices/virtualdjSlice';
import { triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { useToast } from '../contexts/ToastContext';
import './VirtualDJSidebar.scss';
import { errMsg } from '../redux/utils/errMsg';

const VirtualDJSidebar = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const {
        sidebarOpen,
        sidebarWidth,
        syncStatus,
        virtualdjPath,
        virtualdjExists,
        virtualdjPlaylists,
        lastSyncTime,
        importing,
        exporting,
        error
    } = useSelector(state => state.virtualdj);

    const authUserId = useSelector((state) => state.auth?.user?.id) || null;
    const maybeNudgeAutoSync = useAutoSyncNudge('virtualdj', { label: 'Virtual DJ', logo: virtualdjLogo });
    const [isResizing, setIsResizing] = useState(false);
    useSidebarResizing(isResizing);
    const sidebarRef = useRef(null);
    const widthRef = useRef(sidebarWidth);
    widthRef.current = sidebarWidth;
    const [hasCheckedVirtualDJ, setHasCheckedVirtualDJ] = useState(false);
    const [hasLoadedPlaylists, setHasLoadedPlaylists] = useState(false);

    // Load persisted state on mount
    useEffect(() => {
        try {
            // "Last synced" is not read here: it belongs to the account and is
            // hydrated from the main process by DjSyncStateHydrator.
            const virtualdjPath = localStorage.getItem('virtualdj.path') || null;
            const virtualdjPlaylists = JSON.parse(localStorage.getItem('virtualdj.playlists')) || [];

            dispatch(loadPersistedVirtualDJState({
                virtualdjPath,
                virtualdjPlaylists
            }));
        } catch (e) {
            console.error('Failed to load persisted virtualdj state:', e);
            dispatch(loadPersistedVirtualDJState({}));
        }
    }, [dispatch]);

    // Save state on app close
    useEffect(() => {
        const saveOnClose = () => {
            try {
                localStorage.setItem('virtualdj.path', virtualdjPath);
                localStorage.setItem('virtualdj.playlists', JSON.stringify(virtualdjPlaylists));
            } catch (e) {
                // Silently fail
            }
        };

        window.addEventListener('beforeunload', saveOnClose);
        return () => window.removeEventListener('beforeunload', saveOnClose);
    }, [virtualdjPath, virtualdjPlaylists]);

    // Load imported playlists from database
    const loadImportedPlaylists = async (forceRefresh = false) => {
        if (!forceRefresh && virtualdjPlaylists.length > 0) {
            return virtualdjPlaylists;
        }

        try {
            // Pass `false` — display only needs name + trackCount. Asking
            // for tracks here would IPC every track in every playlist.
            const allPlaylists = await window.electron.db.playlists.getAll(false);
            const vdjImported = allPlaylists.filter(p =>
                (p.apps && p.apps.includes('virtualdj')) || (p.description && p.description.includes('Imported from Virtual DJ'))
            );
            dispatch(setVirtualDJPlaylists(vdjImported));
            return vdjImported;
        } catch (err) {
            console.error('[VirtualDJSidebar] Error loading imported playlists:', err);
            return [];
        }
    };

    // On sidebar open: use cached data, only fetch if needed
    useEffect(() => {
        if (!sidebarOpen) return;

        // Skip DB fetch if we already checked (even if no playlists found)
        if (hasLoadedPlaylists || virtualdjPlaylists.length > 0) {
            if (syncStatus !== 'partial') dispatch(setVirtualDJSyncStatus(virtualdjPlaylists.length > 0 ? 'synced' : 'not_synced'));

            if (!hasCheckedVirtualDJ) {
                checkVirtualDJ();
                setHasCheckedVirtualDJ(true);
            }
            return;
        }

        // No cached data - need to load from database
        const initialize = async () => {
            if (!hasCheckedVirtualDJ) {
                dispatch(setVirtualDJSyncStatus('checking'));
            }

            // Run both checks in parallel for faster load
            const [playlists] = await Promise.all([
                loadImportedPlaylists(),
                !hasCheckedVirtualDJ ? checkVirtualDJ() : Promise.resolve()
            ]);

            setHasLoadedPlaylists(true);

            if (!hasCheckedVirtualDJ) {
                setHasCheckedVirtualDJ(true);
            }

            if (syncStatus !== 'partial') dispatch(setVirtualDJSyncStatus(playlists.length > 0 ? 'synced' : 'not_synced'));
        };

        initialize();
    }, [sidebarOpen]);

    // Handle resize
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            // The sidebar sits right of the icon rail (position:fixed with a
            // left offset) — width is cursor minus its left edge, not raw clientX.
            const left = sidebarRef.current?.getBoundingClientRect().left ?? 0;
            const newWidth = e.clientX - left;
            if (newWidth >= DJ_SIDEBAR_MIN_WIDTH && newWidth <= DJ_SIDEBAR_MAX_WIDTH) {
                dispatch(setVirtualDJSidebarWidth(newWidth));
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            persistDjSidebarWidth(widthRef.current);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, dispatch]);

    const checkVirtualDJ = async () => {
        try {
            const result = await window.electron.virtualdj.checkExists();
            dispatch(setVirtualDJPath(result.path));
            dispatch(setVirtualDJExists(result.exists));
            return !!result.exists;
        } catch (err) {
            console.error('Error checking Virtual DJ:', err);
            dispatch(setVirtualDJError(err.message));
            return false;
        }
    };

    // Re-scan must SAY what it found — a re-check that ends in the same
    // "not found" card changes nothing on screen and reads as a dead button.
    const rescanVirtualDJ = async () => {
        const found = await checkVirtualDJ();
        if (found) toast.success('Virtual DJ library found');
        else toast.info('Still no Virtual DJ library in the usual folders — if yours lives somewhere else, use Locate\u2026');
    };

    // opts.playlistKeys — explicit picker selection (SyncFromAppModal).
    // Without it, the persisted exclusions apply, so one-click re-syncs
    // never re-import playlists the user unchecked.
    const handleImport = async (opts = {}) => {
        const playlistKeys = Array.isArray(opts.playlistKeys) ? opts.playlistKeys : null;
        const excludeKeys = playlistKeys ? [] : getImportExclusions('virtualdj', authUserId);
        const wasFirstSync = !lastSyncTime; // captured before setVirtualDJLastSyncTime below
        dispatch(setVirtualDJImporting(true));
        const importStartedAt = Date.now();
        try {
            const result = await window.electron.virtualdj.importPlaylists({
                playlistKeys: playlistKeys || undefined,
                excludeKeys: excludeKeys.length ? excludeKeys : undefined,
            });

            // Stopped from the picker: playlists imported before the stop are
            // COMPLETE (each commits together with its tracks); the rest —
            // and the sync-complete mark — wait for the next run.
            if (result?.canceled) {
                dispatch(triggerPlaylistRefresh());
                const kept = await loadImportedPlaylists(true);
                // A stop that changed nothing leaves the previous state true:
                // a synced library stays "synced", not "incomplete". Only a
                // run that actually landed content degrades the badge.
                const stopChanged = (result.imported ?? 0) + (result.updated ?? 0) + (result.tracksCreated ?? 0) + (result.tracksUpdated ?? 0) > 0;
                if (stopChanged || syncStatus !== 'synced') dispatch(setVirtualDJSyncStatus(kept.length ? 'partial' : 'not_synced'));
                track('dj_import_canceled', {
                    app: 'virtualdj',
                    playlists_imported: result.imported ?? 0,
                    tracks_created: result.tracksCreated ?? 0,
                    tracks_updated: result.tracksUpdated ?? 0,
                    duration_ms: Date.now() - importStartedAt,
                });
                toast.info(formatDjImportStopped(result));
                return;
            }

            dispatch(setVirtualDJLastSyncTime(new Date().toISOString()));
            dispatch(setVirtualDJSyncStatus('synced'));
            dispatch(triggerPlaylistRefresh());

            await loadImportedPlaylists(true);

            track('dj_playlists_imported', {
                app: 'virtualdj',
                mode: playlistKeys ? 'selected' : 'all',
                excluded_count: excludeKeys.length,
                imported: result.imported ?? 0,
                updated: result.updated ?? 0,
                tracks_created: result.tracksCreated ?? 0,
                tracks_updated: result.tracksUpdated ?? 0,
                from: opts.from || 'sidebar',
                duration_ms: Date.now() - importStartedAt,
            });
            if (!maybeNudgeAutoSync(result, opts.from, { firstSync: wasFirstSync })) {
                toast.success(formatDjImportSummary(result, { app: 'Virtual DJ' }));
            }
        } catch (err) {
            console.error('Error importing from Virtual DJ:', err);
            toast.error(`Import failed: ${errMsg(err)}`);
            // Playlists commit whole, so a failure can still leave complete
            // imports behind — keep them visible; the badge says incomplete.
            const kept = await loadImportedPlaylists(true);
            dispatch(setVirtualDJSyncStatus(kept.length ? 'partial' : 'error'));
            dispatch(setVirtualDJError(err.message));
        } finally {
            dispatch(setVirtualDJImporting(false));
        }
    };

    // "Sync now" from the toast card / OS notification opens this sidebar and
    // asks it to import once it's mounted.
    const [showSend, setShowSend] = useState(false);
    const [showImportPicker, setShowImportPicker] = useState(false);

    // Local playlists are owner-scoped (migration 012): on account switch or
    // session restore, drop the previous account's cached list and refetch.
    const prevUserRef = useRef(authUserId);
    useEffect(() => {
        if (prevUserRef.current === authUserId) return;
        prevUserRef.current = authUserId;
        dispatch(setVirtualDJPlaylists([]));
        if (sidebarOpen) loadImportedPlaylists(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUserId]);
    const syncRequested = useSelector((state) => !!state.djLibraryChanges?.syncRequested?.virtualdj);
    const pendingDiff = useSelector((state) => state.djLibraryChanges?.pending?.virtualdj?.diff);
    useEffect(() => {
        if (!syncRequested) return;
        dispatch(clearSyncRequest('virtualdj'));
        // A change that includes never-imported playlists is a choice, not a
        // one-click action — open the picker (new rows start unchecked) so
        // hundreds of unimported playlists can't flood in behind one click.
        // Changes to already-synced playlists alone stay one-click.
        if (pendingDiff?.newPlaylists?.length) setShowImportPicker(true);
        else handleImport({ from: 'watcher_card' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncRequested]);

    const handleExport = async () => {
        let playlistsToExport = [];
        try {
            const allPlaylists = await window.electron.db.playlists.getAll(true);
            playlistsToExport = allPlaylists;
        } catch (err) {
            console.error('Error fetching playlists:', err);
            toast.error('Failed to fetch playlists');
            return;
        }

        if (playlistsToExport.length === 0) {
            toast.warning('No playlists to export');
            return;
        }

        const totalTracks = playlistsToExport.reduce((sum, p) => sum + (p.tracks?.length || 0), 0);

        const result = await window.electron.dialog.showOpenDialog({
            title: 'Select folder to export playlists for Virtual DJ',
            properties: ['openDirectory', 'createDirectory']
        });

        if (result.canceled || !result.filePaths.length) return;

        const outputDir = result.filePaths[0];

        const confirmed = await toast.confirm(
            `This will export ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} with ${totalTracks} track${totalTracks !== 1 ? 's' : ''} as M3U files to:\n\n${outputDir}\n\nTo import in Virtual DJ:\nDrag the M3U files into Virtual DJ's browser.`,
            'Export for Virtual DJ',
            { confirmText: 'Export', confirmVariant: 'primary' }
        );

        if (!confirmed) return;

        dispatch(setVirtualDJExporting(true));
        try {
            await window.electron.virtualdj.exportPlaylists(playlistsToExport, outputDir);
            toast.success(`Exported ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} to ${outputDir}`);
        } catch (err) {
            console.error('Error exporting playlists:', err);
            toast.error(`Export failed: ${errMsg(err)}`);
        } finally {
            dispatch(setVirtualDJExporting(false));
        }
    };

    const formatSyncTime = (isoString) => {
        if (!isoString) return 'Never';
        const date = new Date(isoString);
        // Persisted "null"/garbage (fresh profiles) must not show "Invalid Date".
        return isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
    };

    if (!sidebarOpen) return null;

    // Which imported playlists the user excluded from sync — kept locally by
    // design, skipped by re-syncs. localStorage read per render is cheap; the
    // Manage-imports picker stays the source of truth.
    const excludedKeySet = new Set(getImportExclusions('virtualdj', authUserId));
    const isExcludedFromSync = (p) => { const k = localPlaylistSelectionKey('virtualdj', p); return !!k && excludedKeySet.has(k); };

    return (
        <div ref={sidebarRef} className={`virtualdj-sidebar${isResizing ? ' resizing' : ''}`} style={{ width: `${sidebarWidth}px` }}>
            <SendToAppModal app="virtualdj" show={showSend} onClose={() => setShowSend(false)} onSent={() => loadImportedPlaylists?.(true)} />
            <SyncFromAppModal app="virtualdj" show={showImportPicker} onClose={() => setShowImportPicker(false)} userId={authUserId} onImport={(keys) => handleImport({ playlistKeys: keys, from: 'picker' })} />
            <div className="sidebar-header">
                <div className="header-title">
                    <h4>Virtual DJ Sync</h4>
                    <button
                        className="sidebar-collapse-btn"
                        onClick={() => dispatch(toggleVirtualDJSidebar())}
                        title="Hide Virtual DJ"
                    >
                        ‹
                    </button>
                </div>

                <div className="sync-status">
                    {syncStatus === 'checking' && (
                        <Badge bg="secondary">
                            <Spinner animation="border" size="sm" /> Checking...
                        </Badge>
                    )}
                    {syncStatus === 'synced' && <Badge bg="success">Playlists imported</Badge>}
                    {/* Stopped or failed mid-import: whole playlists are in
                        (each commits with its tracks) but the mirror isn't
                        complete yet — neither "imported" nor "no playlists"
                        is true, so say the third thing. */}
                    {syncStatus === 'partial' && <Badge bg="warning" text="dark">Sync incomplete</Badge>}
                    {/* "Not Synced" measured PLAYLISTS but sat beside a track
                        count, so a library with thousands of tracks imported
                        read as not synced at all — two true statements that
                        looked like a contradiction. Say which one this is. */}
                    {syncStatus === 'not_synced' && virtualdjExists && (
                        <Badge bg="secondary">No playlists imported</Badge>
                    )}
                    {syncStatus === 'not_synced' && !virtualdjExists && (
                        <Badge bg="secondary">Not Found</Badge>
                    )}
                    {syncStatus === 'error' && <Badge bg="danger">Error</Badge>}
                </div>
            </div>

            <div className="sidebar-content">
                {(syncStatus === 'synced' || syncStatus === 'partial') ? (
                    <div className="synced-view">
                        <div className="sync-info">
                            <DjChangeBanner app="virtualdj" onSync={() => handleImport({ from: 'banner' })} syncing={importing} />
                            <p className="last-sync">Last synced: {formatSyncTime(lastSyncTime)}</p>
                            <p className="virtualdj-path" title={virtualdjPath}>{wrappablePath(virtualdjPath)}</p>
                        </div>

                        <AutoSyncToggle app="virtualdj" label="Virtual DJ" />

                        <DjLibraryMeter app="virtualdj" exists={virtualdjExists} userId={authUserId} importing={importing} onManage={() => setShowImportPicker(true)} />

                        <div className="section">
                            <h5>Imported Playlists ({virtualdjPlaylists.length})</h5>
                            {virtualdjPlaylists.length > 0 ? (
                                <ListGroup variant="flush" className="playlist-list">
                                    {virtualdjPlaylists.map((playlist) => (
                                        <ListGroup.Item key={playlist.id} className="playlist-item">
                                            <span className="playlist-name">{playlist.name}</span>
                                            {isExcludedFromSync(playlist) && <span className="dj-not-syncing-tag">not syncing</span>}
                                            <Badge bg="secondary" className="track-count">
                                                {playlist.trackCount || 0} tracks
                                            </Badge>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <p className="empty-message">No playlists imported yet</p>
                            )}
                        </div>

                        <div className="action-buttons">
                            <Button
                                variant="outline-primary"
                                onClick={() => handleImport({ from: 'resync' })}
                                disabled={importing}
                                className="action-btn"
                            >
                                {importing ? (
                                    <><Spinner animation="border" size="sm" /> Re-importing...</>
                                ) : (
                                    'Re-sync from Virtual DJ'
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={importing}
                                className="action-btn"
                            >
                                Manage imports…
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowSend(true)}
                                className="action-btn"
                            >
                                Send playlists to Virtual DJ…
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="not-synced-view">
                        <div className="section detection-section">
                            <h5>Virtual DJ Library</h5>
                            {virtualdjExists ? (
                                <div className="detected">
                                    <div className="status-icon success">&#10003;</div>
                                    <div className="status-text">
                                        <p className="status-label">Virtual DJ library found</p>
                                        <p className="virtualdj-path" title={virtualdjPath}>{wrappablePath(virtualdjPath)}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="not-detected">
                                    <div className="status-icon warning">!</div>
                                    <div className="status-text">
                                        <p className="status-label">Virtual DJ not found</p>
                                        <p className="hint">Make sure Virtual DJ is installed</p>
                                    </div>
                                </div>
                            )}
                            <div className="detection-actions">
                                {/* Re-scan is a retry for FAILED detection — once the
                                    library is found it's noise next to "Change
                                    location…" (two look-alike text links). */}
                                {!virtualdjExists && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={rescanVirtualDJ}
                                        className="refresh-btn"
                                        title="Search the usual locations for your Virtual DJ library again"
                                    >
                                        Re-scan
                                    </Button>
                                )}
                                <DjLocationControls app="virtualdj" label="Virtual DJ" exists={hasCheckedVirtualDJ ? virtualdjExists : null} onChanged={checkVirtualDJ} />
                            </div>
                        </div>

                        <div className="section import-section">
                            <h5>Import from Virtual DJ</h5>
                            <div className="safety-warning">
                                <span className="warning-icon">&#9432;</span>
                                <span>Reads your Virtual DJ database. Your library will NOT be modified.</span>
                            </div>

                            <DjLibraryMeter compact app="virtualdj" exists={virtualdjExists} userId={authUserId} />

                            <Button
                                variant="primary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={!virtualdjExists || importing}
                                className="action-btn import-btn"
                            >
                                {importing ? (
                                    <><Spinner animation="border" size="sm" /> Importing...</>
                                ) : (
                                    'Import from Virtual DJ'
                                )}
                            </Button>
                        </div>

                        <div className="section export-section">
                            <h5>Send to Virtual DJ</h5>
                            <p className="section-hint">Put MixGoat playlists into your Virtual DJ library — pick which ones, only local files are sent.</p>
                            <Button variant="secondary" onClick={() => setShowSend(true)} className="action-btn export-btn">
                                Send playlists to Virtual DJ…
                            </Button>
                        </div>

                    </div>
                )}
                {/* Both views: a failed re-sync must not hide the imported
                    playlists it didn't touch. */}
                {error && (
                    <ErrorBanner
                        variant="inline"
                        error={error}
                        surface="virtualdj_sync"
                        onRetry={() => {
                            dispatch(setVirtualDJError(null));
                            if (syncStatus === 'error' || syncStatus === 'partial') handleImport();
                            else checkVirtualDJ();
                        }}
                    />
                )}
            </div>

            <div className="sidebar-resizer" onMouseDown={() => setIsResizing(true)} />
        </div>
    );
};

export default VirtualDJSidebar;
